/**
 * People and their access.
 *
 * The one screen in the app that is not offline-first. Accounts are not part of
 * the replicated catalog - a device holding the roster of everyone who can sign
 * in would be a liability, not a feature - so this reads straight from the
 * server and says so plainly when there is no connection.
 *
 * Administrators only, enforced on the server as well as here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KeyRound,
  MoreHorizontal,
  Search,
  ShieldCheck,
  TriangleAlert,
  UserPlus,
  Users as UsersIcon,
} from 'lucide-react';
import {
  ApiClientError,
  RESTRICTED_BY_CHECKPOINT,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  UserRole,
  type User,
} from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Dialog, ConfirmDialog } from '../../components/ui/dialog.js';
import { Field, Input, Select } from '../../components/ui/field.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../../components/ui/menu.js';
import { useMasterOptions } from '../../db/queries.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import {
  createUser,
  deleteUser,
  isNetworkError,
  listUsers,
  updateUser,
} from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { RolePermissionsDialog } from './RolePermissionsDialog.js';

const MIN_PASSWORD = 8;
const ROLE_OPTIONS = UserRole.map((r) => ({ value: r, label: ROLE_LABELS[r] }));

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[] | undefined>();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [creating, setCreating] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [settingPassword, setSettingPassword] = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setUsers(await listUsers());
    } catch (err) {
      setUsers([]);
      setLoadError(
        isNetworkError(err)
          ? 'No connection. People can only be managed online.'
          : err instanceof ApiClientError
            ? err.message
            : 'Could not load the people list.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!users) return undefined;
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        ROLE_LABELS[u.role].toLowerCase().includes(q),
    );
  }, [users, search]);

  // Named from the replicated checkpoint table, so the list reads "Dispatch
  // Gate" rather than a row of uuids.
  const checkpointOptions = useMasterOptions('checkpoints');
  const checkpointNames = useMemo(
    () => new Map((checkpointOptions ?? []).map((c) => [c.value, c.label])),
    [checkpointOptions],
  );

  /**
   * Set a role straight from the row menu.
   *
   * Moving someone off a scanning role clears their checkpoint assignment: the
   * ids would otherwise sit on the record meaning nothing until the day they
   * are moved back onto a gate and silently inherit an old posting.
   */
  async function quickSetRole(user: User, role: User['role']) {
    setLoadError(null);
    const scans = role === 'GATE_SECURITY' || role === 'LOCATION_SUPERVISOR';
    try {
      await updateUser(user.id, {
        role,
        assignedCheckpointIds: scans ? (user.assignedCheckpointIds ?? []) : [],
      });
      await load();
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError
          ? err.message
          : isNetworkError(err)
            ? 'No connection. People can only be managed online.'
            : 'Could not change the role.',
      );
    }
  }

  const columns: Array<Column<User>> = [
    {
      key: 'name',
      header: 'Name',
      primary: true,
      sortValue: (u) => u.name,
      render: (u) => (
        <span className="font-medium">
          {u.name}
          {u.id === me?.id ? <span className="ml-2 text-xs text-muted-foreground">you</span> : null}
        </span>
      ),
    },
    { key: 'email', header: 'Email', sortValue: (u) => u.email, render: (u) => u.email },
    {
      key: 'role',
      header: 'Role',
      sortValue: (u) => ROLE_LABELS[u.role],
      render: (u) => ROLE_LABELS[u.role],
    },
    {
      key: 'gates',
      header: 'Gates',
      hideOnMobile: true,
      sortValue: (u) => u.assignedCheckpointIds?.length ?? 0,
      render: (u) => {
        if (!RESTRICTED_BY_CHECKPOINT.has(u.role)) return <span className="text-subtle-foreground">-</span>;
        const ids = u.assignedCheckpointIds ?? [];
        // An unrestricted gate operator is the thing an administrator most needs
        // to spot from the list, so it is called out rather than left blank.
        if (ids.length === 0) return <Badge tone="warning">Any checkpoint</Badge>;
        const names = ids
          .map((id) => checkpointNames.get(id))
          .filter(Boolean)
          .join(', ');
        return (
          <span className="text-sm" title={names}>
            {ids.length === 1 ? (names || '1 checkpoint') : `${ids.length} checkpoints`}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      sortValue: (u) => (u.isActive === false ? 1 : 0),
      render: (u) =>
        u.isActive === false ? (
          <Badge tone="warning">Deactivated</Badge>
        ) : (
          <Badge tone="success">Active</Badge>
        ),
    },
  ];

  return (
    <Page
      title="People"
      description="Who can sign in, and what each of them may do."
      actions={
        <>
          <Button variant="secondary" onClick={() => setShowPermissions(true)}>
            <ShieldCheck className="size-4" /> What each role can do
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)}>
            <UserPlus className="size-4" /> Add person
          </Button>
        </>
      }
      toolbar={
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or role…"
            className="pl-9"
            aria-label="Search people"
          />
        </div>
      }
    >
      {loadError ? (
        <p
          role="alert"
          className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger"
        >
          {loadError}
          <Button size="sm" variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </p>
      ) : null}

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(u) => u.id}
        onRowClick={(u) => setEditing(u)}
        initialSort={{ key: 'name', direction: 'asc' }}
        rowActions={(u) => (
          <Menu
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Actions for ${u.name}`}>
                <MoreHorizontal className="size-4" />
              </Button>
            }
          >
            <MenuLabel>Change role to</MenuLabel>
            {/* Changing somebody's role is the commonest thing done on this
                screen and it was four clicks behind a form. It is one click
                here; the form stays for everything else. */}
            {ROLE_OPTIONS.filter((r) => r.value !== u.role).map((r) => (
              <MenuItem key={r.value} onSelect={() => void quickSetRole(u, r.value as User['role'])}>
                {r.label}
              </MenuItem>
            ))}
            <MenuSeparator />
            <MenuItem onSelect={() => setEditing(u)}>Edit person…</MenuItem>
            <MenuItem onSelect={() => setSettingPassword(u)}>Set password</MenuItem>
            {/* Locking yourself out is the one mistake this screen can make
                irreversible without a shell on the server. */}
            {u.id === me?.id ? null : (
              <MenuItem destructive onSelect={() => setConfirmDelete(u)}>
                Deactivate
              </MenuItem>
            )}
          </Menu>
        )}
        empty={
          search ? (
            <EmptyState
              icon={<Search />}
              title="Nobody matches"
              description={`No one matches "${search}".`}
              action={
                <Button variant="secondary" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<UsersIcon />}
              title="No people yet"
              description="Add the first account."
            />
          )
        }
      />

      <RolePermissionsDialog open={showPermissions} onOpenChange={setShowPermissions} />

      <UserFormDialog
        open={creating}
        onOpenChange={setCreating}
        onSaved={() => void load()}
      />
      <UserFormDialog
        key={editing?.id ?? 'edit'}
        user={editing ?? undefined}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={() => void load()}
      />
      <SetPasswordDialog
        key={settingPassword?.id ?? 'pw'}
        user={settingPassword}
        open={!!settingPassword}
        onOpenChange={(o) => !o && setSettingPassword(null)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Deactivate ${confirmDelete?.name}?`}
        description="They will not be able to sign in. Their scan history stays exactly as it is."
        confirmLabel="Deactivate"
        destructive
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteUser(confirmDelete.id);
          setConfirmDelete(null);
          void load();
        }}
      />
    </Page>
  );
}

/** Add someone, or change their name, role and whether they can sign in. */
function UserFormDialog({
  user,
  open,
  onOpenChange,
  onSaved,
}: {
  user?: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const isEdit = !!user;
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<User['role']>(user?.role ?? 'VIEWER');
  const [isActive, setIsActive] = useState(user?.isActive !== false);
  const [checkpointIds, setCheckpointIds] = useState<string[]>(user?.assignedCheckpointIds ?? []);
  const [locationId, setLocationId] = useState<string>(user?.assignedLocationId ?? '');

  // Checkpoints and locations are replicated to this device by the sync engine,
  // so the pickers work from local data and need no extra request.
  const checkpoints = useMasterOptions('checkpoints');
  const locations = useMasterOptions('locations');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isEdit && password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setSaving(true);
    try {
      // Assignments only mean something for the two roles that are narrowed by
      // them; sending them for an admin would leave misleading rows behind if
      // that person were later moved onto a gate.
      const assignedCheckpointIds = RESTRICTED_BY_CHECKPOINT.has(role) ? checkpointIds : [];
      const assignedLocationId = HAS_HOME_LOCATION.has(role) ? locationId || null : null;
      if (isEdit) {
        await updateUser(user.id, { name, role, isActive, assignedCheckpointIds, assignedLocationId });
      } else {
        await createUser({
          name,
          email,
          password,
          role,
          assignedCheckpointIds,
          assignedLocationId,
        });
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else if (isNetworkError(err)) setError('No connection. People can only be managed online.');
      else setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={isEdit ? `Edit ${user.name}` : 'Add person'}
      description={
        isEdit
          ? 'Change their name, role, or whether they can sign in.'
          : 'They can sign in as soon as you save.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="user-form" loading={saving}>
            {isEdit ? 'Save' : 'Add person'}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>

        {isEdit ? null : (
          <>
            <Field label="Email" required hint="This is what they sign in with.">
              <Input
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field
              label="Password"
              required
              hint={`At least ${MIN_PASSWORD} characters. Tell them to change it once they are in.`}
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
          </>
        )}

        <Field label="Role" hint={ROLE_DESCRIPTIONS[role]}>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as User['role'])}
            options={ROLE_OPTIONS}
          />
        </Field>

        {RESTRICTED_BY_CHECKPOINT.has(role) ? (
          <Field
            label="Gates they may scan at"
            hint="Tick every checkpoint this person works. The scanner shows them only these."
          >
            <CheckpointPicker
              options={checkpoints}
              selected={checkpointIds}
              onChange={setCheckpointIds}
            />
          </Field>
        ) : null}

        {HAS_HOME_LOCATION.has(role) ? (
          <Field
            label="Home location"
            hint="Where this person normally works. Used as the default on their screens."
          >
            <Select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="Not set"
              options={locations ?? []}
            />
          </Field>
        ) : null}

        {isEdit ? (
          <Field label="Can sign in">
            <Select
              value={isActive ? 'yes' : 'no'}
              onChange={(e) => setIsActive(e.target.value === 'yes')}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No, deactivated' },
              ]}
            />
          </Field>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * Set someone else's password.
 *
 * No current password here - the administrator does not know it, which is the
 * situation this exists for. It is the replacement for running a script on the
 * server every time somebody forgets theirs.
 */
function SetPasswordDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (!user) return;

    setSaving(true);
    try {
      await updateUser(user.id, { password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else if (isNetworkError(err)) setError('No connection. Passwords can only be set online.');
      else setError('Could not set the password. Try again.');
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        size="sm"
        title="Password set"
        description={`${user?.name} can sign in with the new password.`}
        footer={
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">
          Pass it on to them directly, and ask them to change it themselves from the menu under
          their name.
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={`Set password for ${user?.name ?? ''}`}
      description="Use this when someone has forgotten theirs."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="set-password" loading={saving}>
            Set password
          </Button>
        </>
      }
    >
      <form id="set-password" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="New password" required hint={`At least ${MIN_PASSWORD} characters.`}>
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Field label="Confirm new password" required>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </Field>

        <p className="flex items-start gap-2 rounded-lg bg-surface-sunken px-3 py-2 text-sm text-muted-foreground">
          <KeyRound className="mt-0.5 size-4 shrink-0" aria-hidden />
          They stay signed in on any device they are already using. The new password applies the
          next time they sign in.
        </p>

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}


/** Roles that have a home site. */
const HAS_HOME_LOCATION = new Set<User['role']>(['LOCATION_SUPERVISOR', 'STORE_KEEPER']);

/**
 * Tick-list of checkpoints.
 *
 * A multi-select <select multiple> is the obvious control and the wrong one:
 * ctrl-clicking to build a list is a desktop skill, and quietly losing the whole
 * selection with one stray click is exactly the failure you cannot afford on a
 * permission screen.
 */
function CheckpointPicker({
  options,
  selected,
  onChange,
}: {
  options: Array<{ value: string; label: string; hint?: string }> | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  if (!options) return <p className="text-sm text-muted-foreground">Loading checkpoints…</p>;
  if (options.length === 0) {
    return (
      <p className="rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
        No checkpoints exist yet. Add them under Sites &amp; Scanning first.
      </p>
    );
  }

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="max-h-52 overflow-y-auto rounded-lg border border-border p-1">
        {options.map((o) => (
          <label
            key={o.value}
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-surface-sunken"
          >
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={() => toggle(o.value)}
              className="size-4 shrink-0"
            />
            <span className="min-w-0 flex-1 truncate text-sm">{o.label}</span>
            {o.hint ? <Code className="shrink-0 text-xs">{o.hint}</Code> : null}
          </label>
        ))}
      </div>
      {/* Silence here would be dangerous: on the server an empty list does not
          mean "nowhere", it means "no restriction". Someone ticking nothing
          because they have not decided yet must not accidentally hand out every
          gate on the site. */}
      {selected.length === 0 ? (
        <p className="flex items-start gap-1.5 text-sm text-warning-foreground">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Nothing ticked means <b>no restriction</b> - they can scan at any checkpoint.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {selected.length} of {options.length} checkpoints.
        </p>
      )}
    </div>
  );
}
